import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { PlusCircleFill, Search } from 'react-bootstrap-icons';
import { styled } from '../../styles/stitches.config';
import { useUser } from '../../contextManager/user.context';
import { useConnections, Connection } from '../../hooks/useConnections';
import { useEvents } from '../../hooks/useEvents';
import { LessonCreationModal } from '../lessons/modals/LessonCreationModal';
import { useTimelineViewingTz } from '../../contextManager/timelineDate.context';
import { getFirstName, getInitial } from '../../utils/name';

const TriggerButton = styled('button', {
  all: 'unset',
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  padding: '0.35rem 0.75rem',
  borderRadius: '0.5rem',
  fontSize: '0.8rem',
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  border: '1px solid $sand11',
  color: '$sand11',
  background: 'transparent',
  transition: 'all 0.2s ease',
  '& svg': {
    fill: 'currentColor',
  },
  '&:hover': {
    background: '$blueHover',
    color: 'var(--colors-blue11)',
    borderColor: 'var(--colors-blue7)',
    '& svg': {
      fill: 'var(--colors-blue11)',
    },
  },
});

const Overlay = styled('div', {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.3)',
  zIndex: 200000,
  display: 'grid',
  placeItems: 'center',
});

const PickerPanel = styled('div', {
  background: 'white',
  borderRadius: 12,
  padding: '1.5rem 1.5rem 2rem',
  width: '22rem',
  maxHeight: '80vh',
  overflow: 'auto',
  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
});

const TutorOption = styled('button', {
  all: 'unset',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  width: '100%',
  padding: '0.65rem 0.75rem',
  borderRadius: 8,
  cursor: 'pointer',
  border: '1px solid #e5e7eb',
  transition: 'all 0.15s',
  '&:hover': {
    background: 'var(--colors-blue2, #eff6ff)',
    borderColor: 'var(--colors-blue7, #93c5fd)',
  },
});

const Avatar = styled('div', {
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  fontSize: '0.8rem',
  fontWeight: 600,
  flexShrink: 0,
});

interface ConnectedTutor {
  id: string;
  name: string;
  profilePhotoUrl?: string;
  profileColor?: string;
  hourlyRate?: number;
  timezone?: string;
  availability?: import('../../types/schemas/abc').AvailabilityObject;
  offersFreeIntroCall?: boolean;
}

export const BookLessonButton: React.FC = () => {
  const { userDetails } = useUser();
  const { connections } = useConnections();
  const { events } = useEvents();
  const navigate = useNavigate();
  const viewingTz = useTimelineViewingTz();
  const [isPickerOpen, setPickerOpen] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState<ConnectedTutor | null>(null);

  // Only show for parents
  if (userDetails?.role !== 'parent') return null;

  // Get connected tutors (exclude support/admin accounts — must have subjects and a rate)
  const connectedTutors: ConnectedTutor[] = connections
    .filter((c: Connection) => c.tutor && c.status !== 'archived' && c.tutor.role === 'tutor' && c.tutor.subjects?.length)
    .map((c: Connection) => c.tutor);

  const handleClick = () => {
    setPickerOpen(true);
  };

  const handleTutorSelect = (tutor: ConnectedTutor) => {
    setSelectedTutor(tutor);
    setPickerOpen(false);
  };

  const getInitials = (name: string) => getInitial(name);

  return (
    <>
      <TriggerButton onClick={handleClick}>
        <PlusCircleFill size={14} />
        Book Lesson
      </TriggerButton>

      {/* Tutor Picker — portaled to body to escape timeline stacking contexts */}
      {isPickerOpen && createPortal(
        <Overlay onClick={() => setPickerOpen(false)}>
          <PickerPanel onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>
              Choose a tutor
            </h3>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {connectedTutors.length === 0 && (
                <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0 0 0.25rem' }}>
                  No tutors yet
                </p>
              )}
              {connectedTutors.map((tutor) => (
                <TutorOption key={tutor.id} onClick={() => handleTutorSelect(tutor)}>
                  <Avatar style={{
                    backgroundColor: tutor.profileColor || '#d1fae5',
                    color: '#065f46',
                  }}>
                    {tutor.profilePhotoUrl
                      ? <img src={tutor.profilePhotoUrl} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                      : getInitials(tutor.name)
                    }
                  </Avatar>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{getFirstName(tutor.name)}</div>
                    {tutor.hourlyRate && (
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        £{Math.ceil(tutor.hourlyRate * 1.25)}/hr
                      </div>
                    )}
                  </div>
                </TutorOption>
              ))}
              <TutorOption onClick={() => { setPickerOpen(false); navigate('/search'); }}>
                <Avatar style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}>
                  <Search size={16} />
                </Avatar>
                <div>
                  <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>Find a tutor</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Search for new tutors</div>
                </div>
              </TutorOption>
            </div>
          </PickerPanel>
        </Overlay>,
        document.body
      )}

      {/* Lesson Creation Modal — portaled to body */}
      {selectedTutor && userDetails && createPortal(
        <LessonCreationModal
          isOpen={!!selectedTutor}
          onClose={() => setSelectedTutor(null)}
          onBackFromStart={() => { setSelectedTutor(null); setPickerOpen(true); }}
          tutorId={selectedTutor.id}
          tutorName={selectedTutor.name}
          user={userDetails}
          tutorHourlyRate={(selectedTutor.hourlyRate ?? 0) * 100}
          tutorTimezone={selectedTutor.timezone}
          tutorAvailability={selectedTutor.availability}
          existingEvents={events}
          tutorOffersIntroCall={selectedTutor.offersFreeIntroCall}
          initialInputTz={viewingTz || undefined}
        />,
        document.body
      )}
    </>
  );
};
